"use client";

import { Experience } from "@ninetailed/experience.js-react";
import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import React, { useCallback, useContext, useEffect, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { ModalsContext } from "@/context/ModalsContext";
import { parseCustomExperience } from "@/lib/experiences";

import CreateLeadForm from "../CreateLeadModal/CreateLeadForm";
import RichTextForBlog from "../RichTextForBlogs/RichTextForBlog";
import LeadFormWrapper from "./LeadFormWrapper";
import { EditorialModuleText as EditorialModuleTextType } from "./type";

export interface EditorialModuleTextProps {
  entry: EditorialModuleTextType;
  kpiHiddenOnMobile?: boolean;
  typeIndex?: number;
}

export default function EditorialModuleText({ entry, kpiHiddenOnMobile, typeIndex }: EditorialModuleTextProps) {
  const locale = useLocale();
  const { setGetInTouchModalOpen, setinnovationLeadModalOpen } = useContext(ModalsContext);
  const t = useTranslations();

  const parsedExperiences = React.useMemo(() => parseCustomExperience(entry), [entry]);

  const [isSo, setIsSo] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const value = localStorage?.getItem("isSo");
      if (value) setIsSo(value === "true");
    }
  }, []);

  const InnerComponent = React.useMemo(() => {
    const Component = (currentEntry?: any) => {
      const activeEntry = currentEntry || entry;
      const finalEntry = activeEntry?.fields ? activeEntry.fields : activeEntry;

      if (finalEntry.type && finalEntry.type !== "editorialModuleText") {
        return <LeadFormWrapper />;
      }

      return (
        <div className="h-full flex flex-col px-m md:pt-xxl md:p-0 md:justify-center md:items-end">
          <div className="md:w-[70%] md:mr-[40px] flex flex-col gap-3xl md:gap-xl rtl:md:mr-0 rtl:md:ml-[40px]">
            <div className="flex flex-col" style={{ marginTop: "16px" }}>
              {finalEntry.title && typeIndex === 1 ? (
                <h1 className="font-normal uppercase font-primary text-l">{finalEntry.title}</h1>
              ) : (
                <p className="font-normal uppercase font-primary text-l">{finalEntry.title}</p>
              )}
              {finalEntry.body && (
                <p className="font-secondary font-secondary-light text-m" style={{ marginTop: "16px" }}>
                  {finalEntry.body}
                </p>
              )}
            </div>
            {finalEntry.kpIs && (
              <div
                className={`${kpiHiddenOnMobile ? "hidden md:flex " : "flex flex-col "} md:flex-row md:justify-between md:gap-xl px-m md:px-0 kpiCustom`}
              >
                {finalEntry.kpIs.map((item: any, index: any) => (
                  <div className="flex justify-between items-center md:flex-col md:items-start md:justify-center md:align-baseline" key={index}>
                    <p className="text-center font-primary text-l text-black-0 md:text-left md:pt-xxs">{item.title}</p>
                    <p className="text-center font-secondary text-m font-secondary-light text-black-0 md:text-left md:pt-xs">{item.subtitle}</p>
                  </div>
                ))}
              </div>
            )}
            {finalEntry.cta && (
              <div className="flex justify-center md:justify-normal md:mt-xl md:mb-l">
                <Button
                  href={
                    (finalEntry.cta.link.linkType === "external" && finalEntry.cta.link.url === "get-in-touch") ||
                    (finalEntry.cta.link.linkType === "external" && finalEntry.cta.link.url === "innovation-lead")
                      ? undefined
                      : getLink(finalEntry.cta, locale)
                  }
                  onClick={() => {
                    if (finalEntry.cta && finalEntry.cta.link.linkType === "external" && finalEntry.cta.link.url === "get-in-touch") {
                      setGetInTouchModalOpen(true);
                    } else {
                      setinnovationLeadModalOpen(true);
                    }
                  }}
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                >
                  {finalEntry.cta.label}
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    };
    return Component;
  }, [entry, locale, kpiHiddenOnMobile, typeIndex, setGetInTouchModalOpen, setinnovationLeadModalOpen]);

  if (isSo) return null;

  return <Experience {...entry} key={`experience-${entry.id}`} id={entry.id as any} component={InnerComponent} experiences={parsedExperiences} />;
}
